import { FC, useMemo, useState } from 'react'
import { DatePicker } from 'antd'
import { RangePickerProps } from 'antd/es/date-picker'
import dayjs from 'dayjs'
import Button from '@UI/New/Button'
import FieldWrapper from '@pages/Admin/CourseSettings/components/FieldWrapper/FieldWrapper'
import SelectField from '@pages/Admin/CourseSettings/components/SelectField/SelectField'
import GroupAllLessonsTable from '@pages/Admin/Reports/GroupReport/components/AllLessonsTable/GroupAllLessonsTable'
import PrivateAllLessonsTable from '@pages/Admin/Reports/PrivateReport/components/AllLessonsTable/PrivateAllLessonsTable'
import ByLessonsTable from '@pages/Admin/Reports/GroupLessonPayment/components/ByLessons/ByLessonsTable'
import cn from './index.module.css'

type LessonStatusTableType = 'group' | 'private' | 'group-lesson-payment'

const TABLES: { key: LessonStatusTableType; label: string }[] = [
  { key: 'group', label: 'Групповые занятия' },
  { key: 'private', label: 'Индивидуальные занятия' },
  { key: 'group-lesson-payment', label: 'Групповые с оплатой по занятиям' },
]

const STATUS_OPTIONS = [
  { label: 'Все', value: 'all' },
  { label: 'Не отчитались', value: '0' },
  { label: 'Проведено', value: '5' },
  { label: 'Отмена педагогом', value: '11' },
  { label: 'Платная отмена', value: '12' },
]

const getSafeDate = (value?: string | null, fallback = dayjs()) => {
  if (!value) return fallback
  const parsedDate = dayjs(value)
  return parsedDate.isValid() ? parsedDate : fallback
}

interface ILessonStatusesTab {
  onReportChange?: () => void
  student?: (Partial<IUser> & {
    childrenDirection?: Array<{
      id?: number | string
      direction_id?: number | string
      tariff_id?: number | string
      group_id?: number | string | null
      group?: { id?: number | string }
      direction?: {
        id?: number | string
        name?: string
        directionTariffs?: Array<{ id: number | string; type?: string }>
      }
    }>
  }) | null
}

const LessonStatusesTab: FC<ILessonStatusesTab> = ({ student, onReportChange }) => {
  const [activeTable, setActiveTable] = useState<LessonStatusTableType>('group')
  const [startDate, setStartDate] = useState(() =>
    dayjs().subtract(30, 'day').format('YYYY-MM-DD'),
  )
  const [endDate, setEndDate] = useState(() => dayjs().format('YYYY-MM-DD'))
  const [status, setStatus] = useState('all')
  const [courseId, setCourseId] = useState('all')
  const studentId = student?.id ?? null
  const directionsRevision = useMemo(
    () =>
      (student?.childrenDirection ?? [])
        .map(
          (order) =>
            `${order.id ?? ''}:${order.group_id ?? order.group?.id ?? ''}`,
        )
        .sort()
        .join('|'),
    [student?.childrenDirection],
  )
  const courseOptions = useMemo(() => {
    const courses = new Map<string, string>()
    student?.childrenDirection?.forEach((order) => {
      const tariffType = order.direction?.directionTariffs?.find(
        (tariff) => String(tariff.id) === String(order.tariff_id),
      )?.type
      const belongsToTable = activeTable === 'group-lesson-payment'
        ? tariffType === 'group_per_lesson'
        : activeTable === 'private'
          ? tariffType === 'hour' || tariffType === 'minute'
          : !!tariffType && !['hour', 'minute', 'group_per_lesson'].includes(tariffType)
      const id = order.direction_id ?? order.direction?.id
      if (belongsToTable && id && order.direction?.name) {
        courses.set(String(id), order.direction.name)
      }
    })
    return [
      { label: 'Все', value: 'all' },
      ...Array.from(courses, ([value, label]) => ({ value, label })),
    ]
  }, [student?.childrenDirection, activeTable])
  const selectedCourseId = courseOptions.some((option) => option.value === courseId)
    ? courseId
    : 'all'

  const onChangeRange: RangePickerProps['onChange'] = (dates) => {
    if (!dates) return
    const [first, last] = dates
    if (!first || !last) return

    setStartDate(first.format('YYYY-MM-DD'))
    setEndDate(last.format('YYYY-MM-DD'))
  }

  const renderTable = () => {
    if (!studentId) return <div className={cn.empty}>Ученик не найден</div>

    if (activeTable === 'group') {
      return (
        <GroupAllLessonsTable
          studentId={studentId}
          startDate={startDate}
          endDate={endDate}
          status={status}
          directionId={selectedCourseId === 'all' ? null : selectedCourseId}
        />
      )
    }

    if (activeTable === 'private') {
      return (
        <PrivateAllLessonsTable
          studentId={studentId}
          startDate={startDate}
          endDate={endDate}
          status={status}
          directionId={selectedCourseId === 'all' ? null : selectedCourseId}
          onReportChange={onReportChange}
        />
      )
    }

    return (
      <ByLessonsTable
        studentId={studentId}
        startDate={startDate}
        endDate={endDate}
        status={status}
        directionId={selectedCourseId === 'all' ? null : selectedCourseId}
        refreshKey={directionsRevision}
        onReportChange={onReportChange}
      />
    )
  }

  return (
    <div className={cn.container}>
      <div className={cn.actions}>
        {TABLES.map((table) => (
          <Button
            key={table.key}
            color={activeTable === table.key ? 'primary' : 'gray'}
            className={cn.actionButton}
            onClick={() => {
              setActiveTable(table.key)
              setCourseId('all')
            }}
          >
            {table.label}
          </Button>
        ))}
      </div>
      <div className={cn.filters}>
        <FieldWrapper label="Период" className={cn.selectWrapper}>
          <DatePicker.RangePicker
            className={cn.datePicker}
            value={[getSafeDate(startDate), getSafeDate(endDate)]}
            onChange={onChangeRange}
            allowClear={false}
            format="DD.MM.YYYY"
          />
        </FieldWrapper>
        <SelectField
          wrapperClassName={cn.selectWrapper}
          label="Статус"
          value={status}
          options={STATUS_OPTIONS}
          onChange={(value) => setStatus(String(value ?? 'all'))}
          size="small"
        />
        <SelectField
          wrapperClassName={cn.selectWrapper}
          label="Курс"
          value={selectedCourseId}
          options={courseOptions}
          onChange={(value) => setCourseId(String(value ?? 'all'))}
          size="small"
        />
      </div>
      {renderTable()}
    </div>
  )
}

export default LessonStatusesTab
